import * as React from 'react'
import { observer } from 'mobx-react'
import { useBrandingSettings } from 'hooks/branding'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentIllustration,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import Button from 'components/Button'
import styles from './BulkArchiveBundleDialog.sass'
import { BulkArchiveBundleViewModel } from './BulkArchiveBundleViewModel'

interface Props {
  vm: BulkArchiveBundleViewModel
}

export default observer(function BulkArchiveBundleDialog({ vm }: Props) {
  const { corporateMode } = useBrandingSettings()
  const count = vm.archivableBundles.length
  const noun = count === 1 ? 'bundle' : 'bundles'

  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          {!corporateMode && (
            <ModalSheetContentIllustration
              src={require('assets/images/img_archive-item.svg')}
              alt="Archive bundles"
            />
          )}
          <h3 className={styles.title}>
            You’re about to archive {count} {noun}
          </h3>
          {getDescriptionDisplay(count)}
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            disabled={vm.archive.pending}
            onClick={vm.close}
            type="default"
            dataTest="bulk-archive-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={vm.archive}
            loading={vm.archive.pending}
            disabled={vm.archive.pending || count === 0}
            type="danger"
            dataTest="bulk-archive-confirm-button"
          >
            Archive {count} {noun}
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

function getDescriptionDisplay(total: number) {
  if (total > 1) {
    return (
      <p className={styles.description}>
        Are you sure you want to archive these{' '}
        <span className={styles.count}>{total} bundles</span>? All future jobs
        will no longer be able to reference them. You can unarchive them later.
      </p>
    )
  }

  return (
    <p className={styles.description}>
      Are you sure you want to archive this{' '}
      <span className={styles.count}>{total} bundle</span>? All future jobs will
      no longer be able to reference it. You can unarchive it later.
    </p>
  )
}
